import React, { useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { tidbitApi } from '@/services/learningManagementApi';
import { usePaginatedList, useMutation } from '@/hooks';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Eye, Edit, Trash2, Sparkles, Search } from 'lucide-react';
import { LoadingState, EmptyState, Pagination } from '@/components/common';
import { formatDate } from '@/utils/dateFormatter';
import type { Tidbit, TidbitStatus } from '@/types/learningManagement';

export const TidbitListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterInterest, setFilterInterest] = useState<string>('all');
  const { hasPermission } = useAdminAuth();

  const canCreate = hasPermission('create', 'tidbit');
  const canEdit = hasPermission('edit', 'tidbit');
  const canDelete = hasPermission('delete', 'tidbit');

  // Build fetch function with conditional API calls
  const fetchTidbitsFn = useMemo(() => {
    return async (params: { page: number; size: number }) => {
      if (searchQuery.trim()) {
        return tidbitApi.search({
          query: searchQuery,
          page: params.page,
          size: params.size,
        });
      } else if (filterCategory !== 'all') {
        return tidbitApi.listByCategory(filterCategory, { page: params.page, size: params.size });
      } else if (filterInterest !== 'all') {
        return tidbitApi.listByInterest(filterInterest, { page: params.page, size: params.size });
      } else {
        return tidbitApi.list({ page: params.page, size: params.size, sortBy: 'createdAt', sortDirection: 'desc' });
      }
    };
  }, [searchQuery, filterCategory, filterInterest]);

  // Use paginated list hook
  const {
    data: tidbits,
    isLoading,
    error,
    pagination,
    setPage,
    setSize,
    refetch,
  } = usePaginatedList<Tidbit>({
    fetchFn: fetchTidbitsFn,
    entityName: 'tidbits',
    suppress404Errors: true,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (tidbitId: string) => {
      await tidbitApi.delete(tidbitId);
    },
    entityName: 'tidbit',
    operation: 'delete',
    onSuccess: () => {
      refetch();
    },
  });

  const handleDelete = async (tidbitId: string) => {
    if (!window.confirm('Are you sure you want to delete this tidbit?')) return;
    await deleteMutation.mutate(tidbitId);
  };

  const handleSearch = () => {
    setPage(0);
    refetch();
  };

  const getStatusColor = (status: TidbitStatus) => {
    switch (status) {
      case 'ACTIVE':
      case 'PUBLISHED':
      case 'FEATURED':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'DRAFT':
      case 'UNDER_REVIEW':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'ARCHIVED':
      case 'EXPIRED':
      case 'CONSUMED':
      case 'VIEWED':
      case 'DELIVERED':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200';
    }
  };

  const tidbitId = (tidbit: Tidbit) => tidbit.tidbitId || tidbit.id || '';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tidbit Management</h1>
          <p className="text-muted-foreground mt-2">Manage all tidbits</p>
        </div>
        {canCreate && (
          <Button asChild>
            <Link to="/admin/tidbits/new">
              <Plus className="mr-2 h-4 w-4" />
              Create Tidbit
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters & Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="md:col-span-2">
              <div className="flex gap-2">
                <Input
                  placeholder="Search tidbits..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button onClick={handleSearch}>
                  <Search className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="TECHNOLOGY">Technology</SelectItem>
                <SelectItem value="SCIENCE">Science</SelectItem>
                <SelectItem value="HEALTH">Health</SelectItem>
                <SelectItem value="BUSINESS">Business</SelectItem>
                <SelectItem value="EDUCATION">Education</SelectItem>
                <SelectItem value="LIFESTYLE">Lifestyle</SelectItem>
                <SelectItem value="ENTERTAINMENT">Entertainment</SelectItem>
                <SelectItem value="ENVIRONMENT">Environment</SelectItem>
                <SelectItem value="POLITICS">Politics</SelectItem>
                <SelectItem value="CULTURE">Culture</SelectItem>
                <SelectItem value="HISTORY">History</SelectItem>
                <SelectItem value="ART">Art</SelectItem>
                <SelectItem value="SPORTS">Sports</SelectItem>
                <SelectItem value="TRAVEL">Travel</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterInterest} onValueChange={setFilterInterest}>
              <SelectTrigger>
                <SelectValue placeholder="All Interest Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Interest Levels</SelectItem>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MODERATE">Moderate</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
                <SelectItem value="FASCINATING">Fascinating</SelectItem>
                <SelectItem value="MIND_BLOWING">Mind Blowing</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tidbits Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tidbits ({pagination.totalElements})</CardTitle>
          <CardDescription>List of all tidbits</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingState message="Loading tidbits..." />
          ) : (error || (tidbits && tidbits.length === 0)) ? (
            <EmptyState
              title="No data found"
              description={error ? "Unable to load tidbits. Please try again or create a new one." : "Get started by creating your first tidbit."}
              icon={Sparkles}
              actions={[
                ...(canCreate ? [{
                  label: 'Create a new one',
                  onClick: () => navigate('/admin/tidbits/new'),
                  variant: 'default' as const,
                }] : []),
                {
                  label: 'Try again',
                  onClick: () => refetch(),
                  variant: 'outline' as const,
                },
              ]}
              searchQuery={searchQuery.trim() || undefined}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Interest Level</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Generated</TableHead>
                    <TableHead>Created At</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tidbits.map((tidbit) => (
                    <TableRow key={tidbitId(tidbit)}>
                      <TableCell className="font-medium">{tidbit.title}</TableCell>
                      <TableCell>{tidbit.category}</TableCell>
                      <TableCell>{tidbit.tidbitType}</TableCell>
                      <TableCell>{tidbit.interestLevel}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(tidbit.status)}`}>
                          {tidbit.status}
                        </span>
                      </TableCell>
                      <TableCell>{tidbit.tidbitGenerated ? 'Yes' : 'No'}</TableCell>
                      <TableCell>
                        {formatDate(tidbit.createdAt)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" asChild>
                            <Link to={`/admin/tidbits/${tidbitId(tidbit)}`}>
                              <Eye className="h-4 w-4" />
                            </Link>
                          </Button>
                          {canEdit && (
                            <Button variant="ghost" size="icon" asChild>
                              <Link to={`/admin/tidbits/${tidbitId(tidbit)}/edit`}>
                                <Edit className="h-4 w-4" />
                              </Link>
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(tidbitId(tidbit))}
                              disabled={deleteMutation.isLoading}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages}
                totalElements={pagination.totalElements}
                size={pagination.size}
                onPageChange={setPage}
                onSizeChange={setSize}
                isLoading={isLoading}
              />
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TidbitListPage;
